import { useEffect, useMemo, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { EntryEditor } from '@/features/editor/components/knowledge/entry-editor'
import { EntryList } from '@/features/editor/components/knowledge/entry-list'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import { orderUserCardFirst } from '@/features/editor/lib/editor-user-card'
import { cn } from '@/lib/utils'
import type {
  KnowledgeEntry,
  KnowledgeKind,
} from '@/features/editor/types/knowledge.types'

type PanelProps = {
  kind: KnowledgeKind
}

export function KnowledgePanel({ kind }: PanelProps) {
  const entries = useEditorStore((s) => s.knowledge[kind])
  const orderedEntries = useMemo(() => {
    return kind === 'characterProfiles' ? orderUserCardFirst(entries) : entries
  }, [entries, kind])

  const addEntry = useEditorStore((s) => s.addEntry)
  const removeEntry = useEditorStore((s) => s.removeEntry)
  const patchEntry = useEditorStore((s) => s.patchEntry)
  const toggleEntryEnabled = useEditorStore((s) => s.toggleEntryEnabled)

  const [selectedId, setSelectedId] = useState<string>('')
  const [editorOpen, setEditorOpen] = useState(false)

  // 只在 entries 变化时校正选中
  useEffect(() => {
    if (selectedId && orderedEntries.some((e) => e.id === selectedId)) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 列表变化后校正选中项，行为符合预期
    setSelectedId(orderedEntries[0]?.id ?? '')
  }, [orderedEntries, selectedId])

  const selected = selectedId
    ? (entries.find((e) => e.id === selectedId) ?? null)
    : null

  function onCreate() {
    const id = addEntry(kind)
    setSelectedId(id)
    setEditorOpen(true)
  }

  function onSelect(id: string) {
    setSelectedId(id)
    setEditorOpen(true)
  }

  function onDelete(id: string) {
    if (id !== selectedId) {
      removeEntry(kind, id)
      return
    }

    const idx = orderedEntries.findIndex((e) => e.id === id)
    const nextId =
      idx >= 0
        ? (orderedEntries[idx + 1]?.id ?? orderedEntries[idx - 1]?.id ?? '')
        : ''

    removeEntry(kind, id)
    setSelectedId(nextId)

    if (!nextId) setEditorOpen(false)
  }

  function onPatch(id: string, patch: Partial<KnowledgeEntry>) {
    patchEntry(kind, id, patch)
  }

  return (
    <>
      {/* 左侧：仍然在 Dialog 内的列表 */}
      <div data-kpanel className='h-full min-h-0'>
        <EntryList
          kind={kind}
          entries={orderedEntries}
          selectedId={selectedId}
          onSelect={onSelect}
          onCreate={onCreate}
          onDelete={onDelete}
          onToggleEnabled={(id) => toggleEntryEnabled(kind, id)}
        />
      </div>

      {/* 右侧：编辑器 Sheet（不受 Dialog 宽度影响） */}
      <Sheet open={editorOpen} onOpenChange={setEditorOpen}>
        <SheetContent
          side='right'
          className={cn(
            // 覆盖默认 sm:max-w-sm（太窄）
            'w-[min(520px,96vw)] sm:max-w-none',
            // 建立高度 & 滚动体系
            'flex min-h-0 flex-col gap-0 p-0'
          )}
        >
          <SheetHeader className='border-border shrink-0 border-b px-5 py-4'>
            <SheetTitle className='text-base font-medium'>编辑条目</SheetTitle>
          </SheetHeader>

          <div className='min-h-0 flex-1 overflow-auto px-5 py-4'>
            <EntryEditor kind={kind} entry={selected} onPatch={onPatch} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
